import { Suspense, lazy, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ThemeProvider } from '@mui/material/styles'
import { TourProvider } from '@reactour/tour'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import '@/i18n/i18n'

import { muiTheme } from '@/theme/muiTheme'
import { AppShell } from '@/components/layout/AppShell'
import { LayoutModeProvider } from '@/components/layout/LayoutModeProvider'
import { useIsBelowMd } from '@/hooks/useLayoutMode'
import { AdminUnavailableNotice } from '@/components/admin/AdminUnavailableNotice'
import { AppErrorBoundary } from '@/components/error/AppErrorBoundary'
import { useConnectivity } from '@/hooks/useConnectivity'
import { useReconnectSync } from '@/hooks/useReconnectSync'
import { useInitialSync } from '@/hooks/useInitialSync'
import { useCustomViews } from '@/hooks/useCustomViews'
import { useCustomStyles } from '@/hooks/useCustomStyles'
import { toStepType } from '@/components/tour/tourSteps'
import { TourContentComponent } from '@/components/tour/ProductTour'
import { useTourSteps } from '@/hooks/useTourSteps'
import { useTour } from '@/hooks/useTour'
import { useTour as useReactour } from '@reactour/tour'
import { useAuthStore } from '@/stores/authStore'
import { BUILD_ID } from '@/utils/buildId'

import { WelcomePage } from '@/pages/WelcomePage'
import { ContentPage } from '@/pages/ContentPage'
import { SearchResultsPage } from '@/pages/SearchResultsPage'
import { LoadIdentityPage } from '@/components/auth/LoadIdentityPage'
import { SplashScreen } from '@/components/auth/SplashScreen'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { ADMIN_PANEL_ROLE_NAMES } from '@/config/roles'
import { useAppInsightsPageTracking } from '@/hooks/useAppInsightsPageTracking'
import { useOverlayPageTracking } from '@/hooks/useOverlayPageTracking'
import { useOfflineSessionTimer } from '@/hooks/useOfflineSessionTimer'
import { OfflineSessionExpiredOverlay } from '@/components/layout/OfflineSessionExpiredOverlay'
import { ContentBundleLoadingOverlay } from '@/components/layout/ContentBundleLoadingOverlay'
import { LanguageSelectionOverlay } from '@/components/layout/LanguageSelectionOverlay'


// Admin shell — lazy-loaded so it is only downloaded when an admin first visits /admin
const AdminShell = lazy(() =>
  import('@/components/admin/AdminShell')
)

/**
 * Mounted inside <BrowserRouter> so it has access to the router context.
 * Fires an App Insights page-view event on every navigation.
 * Tour-driven navigations are tagged so they can be excluded from analytics reports.
 */
function RouteTracker() {
  const { isOpen: isTourOpen } = useReactour()
  useAppInsightsPageTracking(isTourOpen)
  return null
}

function OverlayTracker() {
  useOverlayPageTracking()
  return null
}

function RouteLoadingFallback() {
  const { t } = useTranslation()
  return <div style={{ padding: 40 }}>{t('pages.loading')}</div>
}

/**
 * Gates the admin panel on viewport width.
 *
 * The check sits OUTSIDE <Suspense> and above the lazy() boundary on purpose: a
 * phone must never download the ~1MB admin chunk just to be told the panel will
 * not run. It is also reactive, so resizing below `md` while inside admin swaps
 * the shell for the notice (any unsaved editor state is lost at that point).
 */
function AdminRoute() {
  if (useIsBelowMd()) return <AdminUnavailableNotice />
  return (
    <Suspense fallback={<AdminLoadingFallback />}>
      <AdminShell />
    </Suspense>
  )
}

function AdminLoadingFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '14px', color: 'var(--color-text-tertiary)' }}>
      Loading Admin Panel…
    </div>
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      // Raise gcTime to match the persister maxAge so in-memory cache is not
      // evicted before the persisted snapshot can be used on next load.
      gcTime: 24 * 60 * 60 * 1000, // 24 hours
      retry: 2,
    },
  },
})

// Admin queries must never be cached: always stale and evicted immediately
// when all subscribers unmount. The dehydrateOptions filter below prevents
// them from being written to localStorage.
queryClient.setQueryDefaults(['admin'], { staleTime: 0, gcTime: 0 })

/** Query roots that must never be written to localStorage — private data, or data that goes stale dangerously. */
const PRIVATE_QUERY_KEYS = ['admin', 'custom-view-shares', 'custom-view-notes', 'custom-view-note-history']

// Persist the TanStack Query cache to localStorage so queries like
// useCurrentUser survive full page reloads without a network round-trip.
// This is essential for offline cold-starts: the persisted snapshot is
// rehydrated synchronously before any component renders.
// `buster` is the build id: a snapshot written by an earlier deploy is discarded
// rather than rehydrated into a bundle that may expect a different payload shape.
const queryPersister = createSyncStoragePersister({
  storage: window.localStorage,
})

function UserContentSyncer() {
  useReconnectSync()
  useInitialSync()
  useCustomViews()
  useOfflineSessionTimer()
  return null
}

function ConnectivityMonitor({ children }: { children: React.ReactNode }) {
  const isAdmin = window.location.pathname.startsWith('/admin')
  useConnectivity()
  useCustomStyles()
  return (
    <>
      <OfflineSessionExpiredOverlay />
      {!isAdmin && <UserContentSyncer />}
      {!isAdmin && <ContentBundleLoadingOverlay />}
      {!isAdmin && <LanguageSelectionOverlay />}
      {children}
    </>
  )
}

function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { loadIdentity, isLoading } = useAuthStore()
  const isAdmin = window.location.pathname.startsWith('/admin')

  useEffect(() => {
    loadIdentity()
  }, [loadIdentity])

  if (isLoading) return <SplashScreen />

  return (
    <>
      {!isAdmin && <TourStepsSyncer />}
      {children}
    </>
  )
}

/** Reads the primary colour from the MUI theme so the tour spotlight matches. */
const TOUR_PRIMARY = muiTheme.palette.primary.main ?? 'var(--color-brand-accent)'

/**
 * Syncs remote tour steps into the TourProvider context once they load.
 * Must be rendered *inside* TourProvider so it has access to setSteps.
 * TourProvider only reads the `steps` prop on first render, so this is
 * the only way to update steps without remounting the whole provider.
 */
function TourStepsSyncer() {
  const { setSteps } = useReactour()
  const { data: remoteSteps } = useTourSteps()

  useEffect(() => {
    if (remoteSteps && remoteSteps.length > 0) {
      setSteps?.(remoteSteps.map(toStepType))
    }
  }, [remoteSteps, setSteps])

  return null
}

function TourProviderWrapper({ children }: { children: React.ReactNode }) {
  const { markSeen } = useTour()

  return (
    <TourProvider
      steps={[]}
      components={{ Content: TourContentComponent }}
      onClickClose={({ setIsOpen }) => { markSeen(); setIsOpen(false) }}
      beforeClose={() => markSeen()}
      styles={{
        popover: (base) => ({
          ...base,
          borderRadius: 10,
          padding: '20px 24px',
          maxWidth: 340,
          boxShadow: 'var(--shadow-overlay-strong)',
        }),
        maskArea: (base) => ({ ...base, rx: 6 }),
        dot: (base, state) => ({
          ...base,
          background: state?.current ? TOUR_PRIMARY : undefined,
        }),
      }}
      padding={{ mask: 8, popover: [10, 12] }}
      showBadge={false}
    >
      {children}
    </TourProvider>
  )
}

export default function App() {
  return (
    <ThemeProvider theme={muiTheme}>
      <LayoutModeProvider>
        <AppErrorBoundary>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister: queryPersister,
              // Keep the persisted cache for up to 24 hours — matches gcTime above.
              maxAge: 24 * 60 * 60 * 1000,
              buster: BUILD_ID,
              dehydrateOptions: {
                // custom-view-shares holds other users' usernames and must not sit in localStorage.
                // custom-view-notes is collaborative: a body rehydrated from a day-old snapshot and
                // then saved would silently overwrite whatever everyone else wrote in between.
                shouldDehydrateQuery: (query) =>
                  !PRIVATE_QUERY_KEYS.includes(query.queryKey[0] as string),
              },
            }}
          >
            <TourProviderWrapper>
              <AuthInitializer>
                <ConnectivityMonitor>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <BrowserRouter>
                      <RouteTracker />
                      <OverlayTracker />
                      <Routes>
                        {/* Auth callback route - loads identity after SAML login */}
                        <Route path="/load-identity" element={<LoadIdentityPage />} />

                        <Route element={<AppShell />}>
                          <Route index element={<WelcomePage />} />
                          <Route path="/practice-area/:practiceAreaId" element={<ContentPage />} />
                          <Route path="/practice/:practiceId" element={<ContentPage />} />
                          <Route path="/framework/:frameworkId" element={<ContentPage />} />
  <Route path="/search" element={<SearchResultsPage />} />
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </Route>

                        {/* Admin routes — lazy loaded and protected */}
                        <Route
                          path="/admin/*"
                          element={
                            <AuthGuard requiredRoles={ADMIN_PANEL_ROLE_NAMES}>
                              <AdminRoute />
                            </AuthGuard>
                          }
                        />
                      </Routes>
                    </BrowserRouter>
                  </Suspense>
                </ConnectivityMonitor>
              </AuthInitializer>
            </TourProviderWrapper>
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
            <ToastContainer
              position="bottom-right"
              autoClose={4000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
          </PersistQueryClientProvider>
        </AppErrorBoundary>
      </LayoutModeProvider>
    </ThemeProvider>
  )
}
